import clsx from 'clsx';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { IconName, IconSize } from '../../icons/types';
import type { ActionButtonDropdownMenuAriaHasPopup, ActionButtonDropdownMenuOption, UseActionButtonDropdownMenuParams, UseActionButtonDropdownMenuReturn } from './types';

// Puente temporal: cada clase v2 (`action-button-dropdown-menu*`) se acompaña de
// su gemela legacy (`button-fixed-dropdown*`) porque los SCSS de los consumidores
// (banners, hint, lesson-view…) aún posicionan/estilizan el FAB apuntando a las
// clases del v1. Es el mismo contrato de cascada que mantiene el `Popover` v2 con
// `.dropdown__lib`; la clase legacy se retirará cuando muera el `ButtonFixedDropdown`.
function withLegacy(v2ClassName: string): string {
  const legacy = v2ClassName
    .split(' ')
    .filter(Boolean)
    .map((name) => name.replace('action-button-dropdown-menu', 'button-fixed-dropdown'))
    .join(' ');

  return `${v2ClassName} ${legacy}`;
}

// Consumidores heredados pasan iconSize v1 (`big`, `small`, `default`) que no
// existen en el atom v2 (`xs/sm/md/lg/xl/xxl/3xl/4xl`). Sin filtrar, la var CSS
// inline `var(--ui-icon-size-big)` es inválida y hace que la declaración
// `width: var(--button-icon-size)` sea "invalid at computed-value time" — el
// icono cae a `width: auto` y renderiza al tamaño natural del SVG (mucho más
// grande que 24px).
const VALID_ICON_SIZES: ReadonlySet<IconSize> = new Set<IconSize>(['xs', 'sm', 'md', 'lg', 'xl', 'xxl', '3xl', '4xl']);

// En webviews táctiles (app de tablet) el cierre por ClickAway se dispara con el
// `touchend` y, a continuación, el navegador emite un `click` sintético sobre el
// botón que volvería a abrir el menú ("ghost click"). Ignoramos cualquier
// reapertura que ocurra inmediatamente después de un cierre.
const REOPEN_GUARD_MS = 400;

// El ghost-click solo se produce en táctil. Acotamos el guard a estos
// dispositivos para no bloquear en desktop una reapertura legítima.
function isTouchDevice(): boolean {
  return 'ontouchstart' in globalThis || (globalThis.navigator?.maxTouchPoints ?? 0) > 0;
}

function getIconButton(isOpen: boolean, icon?: IconName, iconClose?: IconName): IconName | undefined {
  if (isOpen && iconClose) {
    return iconClose;
  }

  return icon;
}

export default function useActionButtonDropdownMenu({
  classNameDropdown = '',
  closeOnClickInOption = true,
  customDropdown,
  emitClickEventOpen = () => {},
  fixed = true,
  icon,
  iconClose,
  iconSize,
  onChangeOpen = () => {},
  open,
  rotate = true,
  size = 'l',
}: UseActionButtonDropdownMenuParams): UseActionButtonDropdownMenuReturn {
  const containerButtonRef = useRef<HTMLDivElement>(null);
  const lastCloseAtRef = useRef<number>(0);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isOpen, setIsOpen] = useState<boolean>(Boolean(open));

  const iconButton = useMemo(() => getIconButton(isOpen, icon, iconClose), [isOpen, icon, iconClose]);
  const resolvedIconSize = iconSize && VALID_ICON_SIZES.has(iconSize) ? iconSize : undefined;

  // `aria-haspopup` del disparador según el contenido real del panel: `dialog`
  // cuando se pasa un `customDropdown` (formulario), `menu` para el listado de
  // `options` (el `ListSimpleDropdown` v2 implementa el patrón menú WAI-ARIA:
  // roles/teclado/type-ahead, TAN-9065). Se comprueba `== null` para casar con el
  // `customDropdown ?? <ListSimpleDropdown>` del render, que solo cae al fallback
  // (la lista) con null/undefined.
  const ariaHasPopup: ActionButtonDropdownMenuAriaHasPopup = customDropdown == null ? 'menu' : 'dialog';

  const rootClassName = withLegacy(clsx('action-button-dropdown-menu', `action-button-dropdown-menu--size-${size}`, { 'action-button-dropdown-menu--not-fixed': !fixed }));
  const buttonClassName = withLegacy(
    clsx('action-button-dropdown-menu__button', {
      'action-button-dropdown-menu__button--open': isOpen,
      'action-button-dropdown-menu__button--open-rotate': isOpen && !iconClose && rotate,
      'action-button-dropdown-menu__button--close': !isOpen,
    }),
  );
  const popoverClassName = clsx(withLegacy('action-button-dropdown-menu__dropdown-lib'), classNameDropdown);

  useEffect(() => {
    setAnchorEl(containerButtonRef.current);
    setIsOpen(Boolean(open));
  }, [open]);

  useEffect(() => {
    onChangeOpen(isOpen);
    // `onChangeOpen` se omite de deps a propósito: la identidad del callback del
    // consumidor no debe reejecutar el efecto (paridad con el v1).
  }, [isOpen]);

  function onCloseDropdown(): void {
    lastCloseAtRef.current = Date.now();
    setIsOpen(false);
  }

  function onToggleDropdown(event: ReactMouseEvent): void {
    if (isOpen) {
      onCloseDropdown();
      return;
    }

    // Evita la reapertura inmediata tras un cierre por ClickAway en táctil.
    if (isTouchDevice() && Date.now() - lastCloseAtRef.current < REOPEN_GUARD_MS) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    // El ancla del panel es siempre el contenedor del disparador (no el
    // `currentTarget` del evento), para que coincida con el ancla que fija el
    // efecto cuando la apertura viene por la prop `open` controlada.
    setAnchorEl(containerButtonRef.current);
    setIsOpen(true);
    emitClickEventOpen(event);
  }

  function onClickInOption(item: ActionButtonDropdownMenuOption, event: ReactMouseEvent): void {
    if (item.onClick) {
      item.onClick(item, event);
      if (closeOnClickInOption) {
        onCloseDropdown();
      }
    }
  }

  return {
    anchorEl,
    ariaHasPopup,
    buttonClassName,
    containerButtonRef,
    iconButton,
    isOpen,
    onClickInOption,
    onCloseDropdown,
    onToggleDropdown,
    popoverClassName,
    resolvedIconSize,
    rootClassName,
  };
}
